'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'

import { cn } from '../cn'
import { handlePrimaryActionOnEnter } from './dialog-keyboard'
import { SurfaceCloseButton } from './icon-button-close'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  fullscreen?: boolean
  /**
   * Mobile-only animation origin. Defaults to `bottom` (sheet-style).
   * Use `top` for search-bar dropdowns and other top-bar-attached UIs.
   */
  mobileSlideFrom?: 'top' | 'bottom'
  /**
   * Hide the built-in top-right close button on mobile. Use when the caller
   * supplies its own mobile close affordance (e.g. a back-arrow in the header).
   */
  hideCloseOnMobile?: boolean
  /** Hide the default close button entirely (useful when providing custom close UI). */
  hideCloseButton?: boolean
  /** Optional classes for the dialog overlay. */
  overlayClassName?: string
  /**
   * When true, pressing Enter from non-text-entry dialog chrome triggers the
   * primary action instead of requiring a pointer click.
   */
  primaryActionOnEnter?: boolean
}

const DialogContent = React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  (
    {
      className,
      children,
      fullscreen,
      mobileSlideFrom = 'bottom',
      hideCloseOnMobile,
      hideCloseButton,
      overlayClassName,
      onEscapeKeyDown,
      onKeyDown,
      onOpenAutoFocus,
      primaryActionOnEnter,
      ...props
    },
    ref,
  ) => {
    const contentRef = React.useRef<React.ComponentRef<typeof DialogPrimitive.Content> | null>(null)

    const setRef = React.useCallback(
      (node: React.ComponentRef<typeof DialogPrimitive.Content> | null) => {
        contentRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ;(ref as React.MutableRefObject<React.ComponentRef<typeof DialogPrimitive.Content> | null>).current = node
        }
      },
      [ref],
    )

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={setRef}
          onEscapeKeyDown={(e) => {
            e.stopPropagation()
            onEscapeKeyDown?.(e)
          }}
          onKeyDown={(e) => {
            onKeyDown?.(e)
            if (!e.defaultPrevented && primaryActionOnEnter) {
              handlePrimaryActionOnEnter(e, contentRef.current)
            }
          }}
          onOpenAutoFocus={(e) => {
            onOpenAutoFocus?.(e)
            if (e.defaultPrevented) {
              return
            }
            e.preventDefault()

            const focusContent = () => contentRef.current?.focus({ preventScroll: true })
            if (typeof window === 'undefined') {
              focusContent()
            } else {
              window.requestAnimationFrame(focusContent)
            }
          }}
          className={cn(
            fullscreen
              ? 'fixed inset-0 z-50 flex flex-col border-0 bg-background p-0 gap-0 overflow-hidden focus:outline-none focus-visible:outline-none [&>button]:hidden'
              : cn(
                  'fixed z-50 flex flex-col w-full border bg-background dark:bg-card shadow-lg duration-150 overflow-hidden overscroll-contain focus:outline-none focus-visible:outline-none',
                  'inset-x-0 bottom-0 top-0 rounded-none',
                  'pt-6 px-6 pb-6',
                  'data-[state=open]:animate-in data-[state=closed]:animate-out',
                  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                  mobileSlideFrom === 'top'
                    ? 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top'
                    : 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
                  'sm:inset-auto sm:left-[50%] sm:top-[50%] sm:max-w-lg sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg',
                  'sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
                  '[&>*]:min-w-0',
                ),
            // A <form> wrapping the header/body/footer is otherwise the sole flex
            // child here, and an uncapped one grows with its content: DialogBody's
            // flex-1/overflow-y-auto never engages and overflow-hidden clips the
            // footer off the bottom. Re-establish the column on it so the body
            // scrolls and the footer stays pinned.
            '[&>form]:flex [&>form]:min-h-0 [&>form]:flex-1 [&>form]:flex-col',
            className,
          )}
          {...props}>
          {children}
          {!fullscreen && !hideCloseButton && (
            <DialogPrimitive.Close asChild>
              <SurfaceCloseButton
                className={cn('absolute right-3 top-3', hideCloseOnMobile && 'hidden sm:inline-flex')}
              />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  },
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col space-y-1.5 text-center sm:text-left shrink-0 px-12 sm:pl-0 sm:pr-12', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 overflow-y-auto', '-mx-6 px-6 py-4', className)} {...props} />
)
DialogBody.displayName = 'DialogBody'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-dialog-footer="true"
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2',
      '[&>button]:w-full sm:[&>button]:w-auto',
      'pt-4 pb-[env(safe-area-inset-bottom)] sm:pb-0',
      'shrink-0',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight break-words', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
